import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import {
    Search,
    MessageSquare,
    PlayCircle,
    MapPin,
    CreditCard,
    Megaphone,
    Users,
    Building2,
    Factory,
    Flag,
    Star,
    TrendingUp,
    Calendar,
    CheckCircle,
    Circle,
} from 'lucide-react';

const ICONS = {
    Search,
    MessageSquare,
    PlayCircle,
    MapPin,
    CreditCard,
    Megaphone,
    Users,
    Building2,
    Factory,
    Flag,
    Star,
    TrendingUp,
    Calendar,
    CheckCircle,
};

const num = (i) => String(i + 1).padStart(2, '0');

/**
 * Түүхэн замнал — нүүр хуудасны section.
 * Десктоп дээр хэвтээ, үе шатууд шугамны дээр/доор ээлжилнэ.
 * Мобайл дээр босоо timeline болно.
 */
export default function TimelineSection({ items = [] }) {
    const lang = useSelector((s) => s.system.lang || 'mn');
    const en = lang === 'en';

    if (!items || items.length === 0) return null;

    const pickTitle = (it) => (en && it.titleEn ? it.titleEn : it.title);
    const pickDesc = (it) =>
        en && it.descriptionEn ? it.descriptionEn : it.description;

    const Marker = ({ it, size = 56 }) => {
        const Icon = ICONS[it.icon] || Circle;
        const color = it.color || '#F5C518';
        return (
            <div
                className="flex items-center justify-center rounded-full shadow-lg ring-4 ring-brand-dark shrink-0"
                style={{ width: size, height: size, backgroundColor: color }}
            >
                <Icon className="w-1/2 h-1/2 text-white" strokeWidth={2.2} />
            </div>
        );
    };

    const Photo = ({ it, size = 112 }) =>
        it.imageUrl ? (
            <div
                className="relative rounded-full overflow-hidden ring-4 ring-white/10 shadow-md bg-brand-dark transition-transform duration-300 hover:scale-105"
                style={{ width: size, height: size }}
            >
                <Image
                    src={it.imageUrl}
                    alt={pickTitle(it)}
                    fill
                    sizes="120px"
                    className="object-cover grayscale"
                    unoptimized
                />
            </div>
        ) : null;

    const TextBlock = ({ it, i, align = 'center' }) => (
        <div className={`text-${align}`}>
            <div
                className="font-heading font-bold leading-none"
                style={{ fontSize: 34, color: it.color || '#F5C518' }}
            >
                {num(i)}
            </div>
            <h3 className="font-heading uppercase font-bold text-white text-lg mt-1">
                {pickTitle(it)}
            </h3>
            {it.year && (
                <div className="text-brand-yellow font-bold text-sm mt-0.5">
                    {it.year}
                </div>
            )}
            {pickDesc(it) && (
                <p className="text-white/55 text-xs leading-relaxed mt-2 max-w-[230px] mx-auto line-clamp-3">
                    {pickDesc(it)}
                </p>
            )}
        </div>
    );

    return (
        <section className="py-20 lg:py-28 bg-brand-dark overflow-hidden">
            <div className="max-w-[1280px] mx-auto px-4 lg:px-8">
                {/* Heading */}
                <div className="text-center mb-14 lg:mb-20">
                    <h2 className="font-heading uppercase font-bold text-white text-3xl lg:text-4xl tracking-tight">
                        {en ? 'Project Milestones' : 'Төслийн үе шатууд'}
                    </h2>
                    <div className="mx-auto mt-4 h-1 w-20 bg-brand-yellow" />
                </div>

                {/* ─── Десктоп: хэвтээ timeline ─── */}
                <div className="hidden lg:block relative">
                    {/* Төв хэвтээ шугам */}
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-white/20" />

                    <div className="relative flex">
                        {items.map((it, i) => {
                            const above = i % 2 === 0;
                            return (
                                <div
                                    key={it.id}
                                    className="flex-1 min-w-0 px-2 grid"
                                    style={{
                                        gridTemplateRows: '1fr auto 1fr',
                                        minHeight: 520,
                                    }}
                                >
                                    {/* Дээд талбар */}
                                    <div className="flex flex-col items-center justify-end pb-4">
                                        {above ? (
                                            <>
                                                <TextBlock it={it} i={i} />
                                                <div className="mt-4">
                                                    <Photo it={it} />
                                                </div>
                                                <div className="w-px h-6 bg-white/25 mt-4" />
                                            </>
                                        ) : null}
                                    </div>

                                    {/* Маркер (шугамны дээр) */}
                                    <div className="flex items-center justify-center relative z-10">
                                        <Marker it={it} />
                                    </div>

                                    {/* Доод талбар */}
                                    <div className="flex flex-col items-center justify-start pt-4">
                                        {!above ? (
                                            <>
                                                <div className="w-px h-6 bg-white/25 mb-4" />
                                                <Photo it={it} />
                                                <div className="mt-4">
                                                    <TextBlock it={it} i={i} />
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ─── Мобайл: босоо timeline ─── */}
                <div className="lg:hidden relative pl-4">
                    {/* Босоо шугам */}
                    <div className="absolute left-[27px] top-2 bottom-2 w-[2px] bg-white/20" />
                    <div className="space-y-10">
                        {items.map((it, i) => (
                            <div key={it.id} className="relative flex gap-5">
                                <div className="relative z-10">
                                    <Marker it={it} size={48} />
                                </div>
                                <div className="flex-1 pt-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span
                                            className="font-heading font-bold leading-none"
                                            style={{
                                                fontSize: 26,
                                                color: it.color || '#F5C518',
                                            }}
                                        >
                                            {num(i)}
                                        </span>
                                        {it.imageUrl && (
                                            <div className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/10 bg-brand-dark shrink-0">
                                                <Image
                                                    src={it.imageUrl}
                                                    alt={pickTitle(it)}
                                                    fill
                                                    sizes="48px"
                                                    className="object-cover grayscale"
                                                    unoptimized
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <h3 className="font-heading uppercase font-bold text-white text-base">
                                        {pickTitle(it)}
                                    </h3>
                                    {it.year && (
                                        <div className="text-brand-yellow font-bold text-sm">
                                            {it.year}
                                        </div>
                                    )}
                                    {pickDesc(it) && (
                                        <p className="text-white/55 text-xs leading-relaxed mt-1.5 line-clamp-3">
                                            {pickDesc(it)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Бүх түүх рүү холбоос */}
                <div className="text-center mt-14">
                    <Link
                        href="/project/history"
                        className="inline-flex items-center gap-2 bg-brand-yellow text-brand-dark hover:bg-brand-yellow-dark font-heading uppercase font-semibold text-sm tracking-wider px-7 py-3.5 transition-colors"
                    >
                        {en ? 'View Full History' : 'Бүх түүхийг үзэх'}
                        <span aria-hidden="true">→</span>
                    </Link>
                </div>
            </div>
        </section>
    );
}